import {Dispatch, SetStateAction, useEffect, useRef} from 'react'
import {getSubInvoices} from './_requests'
import {Invoice, InvoiceSubRow} from './_models'

/**
 * «سوابق صورتحساب» are read when a row opens them. Whatever makes the list answer again — a workspace
 * inquiry, a manual status, a send, the send poll — may also have changed that chain on the server: an
 * accepted cancellation, for one, cancels its reference. So every new answer of the list reads the open
 * chains again and forgets the closed ones, which load afresh when they are opened.
 */
export const useRefreshInvoiceNestedRows = (
  listUpdatedAt: number | undefined,
  invoices: Invoice[],
  nestedRows: InvoiceSubRow[],
  setNestedRows: Dispatch<SetStateAction<InvoiceSubRow[]>>
) => {
  const seenUpdatedAt = useRef(listUpdatedAt)
  // Only the newest reading may write: an older one that answers late would bring back older rows.
  const reading = useRef(0)
  const latest = useRef({invoices, nestedRows})
  latest.current = {invoices, nestedRows}

  useEffect(() => {
    if (!listUpdatedAt || listUpdatedAt === seenUpdatedAt.current) return
    seenUpdatedAt.current = listUpdatedAt

    const {invoices: listRows, nestedRows: shownRows} = latest.current
    if (shownRows.length === 0) return

    const hashes = new Map(listRows.map((invoice) => [String(invoice.id), invoice.hsh]))
    const toRead = shownRows.filter((row) => row.isOpen && hashes.get(String(row.id)))
    const kept = new Set(toRead.map((row) => String(row.id)))
    setNestedRows((current) => current.filter((row) => kept.has(String(row.id))))

    const readingId = ++reading.current
    toRead.forEach((row) => {
      const writeRow = (change: Partial<InvoiceSubRow>) => {
        if (reading.current !== readingId) return
        setNestedRows((current) =>
          current.map((item) => (String(item.id) === String(row.id) ? {...item, ...change} : item))
        )
      }

      getSubInvoices(hashes.get(String(row.id)) as string).then(
        (rows) => writeRow({rows, refreshFailed: false}),
        // The rows already shown stay, marked as possibly old; the next answer of the list reads again.
        () => writeRow({refreshFailed: true})
      )
    })
  }, [listUpdatedAt, setNestedRows])
}
